import lodash from 'lodash';
import {
    ValueSetRegistry,
    ValueSets
} from '@complion/query';

const {
    FixedValue,
    ScalarValue,
    SelectList,
    MultiselectList,
    ValueList,
    DatasetFixedValue,
    DatasetSingleValue,
    DatasetValueList,
    DatasetMultiselectList,
    DataSourceSingleValue,
    DataSourceMultipleValue,
    DataSourceValueList
} = ValueSets;

const comparisonHandlersByType = {
    [FixedValue.name]: compareFixedValue,
    [ScalarValue.name]: compareScalarValue,
    [SelectList.name]: compareSelectList,
    [MultiselectList.name]: compareMultiSelectList,
    [ValueList.name]: compareValueList,
    [DatasetFixedValue.name]: compareDatasetFixedValue,
    [DatasetSingleValue.name]: compareDatasetSingleValue,
    [DatasetValueList.name]: compareDatasetValueList,
    [DatasetMultiselectList.name]: compareDatasetMultiSelectList,
    [DataSourceSingleValue.name]: compareDataSourceSingleValue,
    [DataSourceMultipleValue.name]: compareDataSourceMultipleValue,
    [DataSourceValueList.name]: compareDataSourceValueList
};

import { getRawValue } from 'common/util/dataTypeValues';

function compareProperties(objectOne, objectTwo, properties) {
    const propertiesOne = lodash.pick(objectOne, properties);
    const propertiesTwo = lodash.pick(objectTwo, properties);

    return lodash.isEqual(propertiesOne, propertiesTwo);
}

function compareValueWithDataType(valueOne, valueTwo, dataType = 'small_text') {
    const rawValueOne = getRawValue(dataType, valueOne);
    const rawValueTwo = getRawValue(dataType, valueTwo);
    if (['datetime', 'dateonly'].includes(dataType)) {
        return rawValueOne.isSame(rawValueTwo);
    } else {
        return lodash.eq(
            rawValueOne,
            rawValueTwo
        );
    }
}

function compareLists(valueListOne, valueListTwo, dataType = 'small_text') {
    if (lodash.size(valueListOne) !== lodash.size(valueListTwo)) {
        return false;
    }
    return lodash.every(valueListOne,
        valueOne => lodash.some(valueListTwo,
            valueTwo => compareValueWithDataType(valueOne, valueTwo, dataType)));
}

function compareFixedValue(valueOne, valueTwo) {
    return compareProperties(valueOne, valueTwo, ['dataType']) &&
        compareValueWithDataType(valueOne.value, valueTwo.value, valueOne.dataType);
}

function compareScalarValue(valueOne, valueTwo) {
    return compareProperties(valueOne, valueTwo, ['dataType']);
}

function compareSelectList(valueOne, valueTwo) {
    return compareProperties(valueOne, valueTwo, ['dataType']) &&
        compareLists(valueOne.values, valueTwo.values, valueOne.dataType);
}

function compareMultiSelectList(valueOne, valueTwo) {
    return compareProperties(valueOne, valueTwo, ['dataType']) &&
        compareLists(valueOne.values, valueTwo.values, valueOne.dataType);
}

function compareValueList(valueOne, valueTwo) {
    return compareProperties(valueOne, valueTwo, ['dataType']) &&
        compareLists(valueOne.values, valueTwo.values, valueOne.dataType);
}

function compareDatasetFixedValue(valueOne, valueTwo) {
    return compareProperties(valueOne, valueTwo, ['dataType']) &&
        compareValueWithDataType(valueOne.value, valueTwo.value, valueOne.dataType);
}

function compareDatasetSingleValue(valueOne, valueTwo) {
    return compareProperties(valueOne, valueTwo, ['dataType']) &&
        compareLists(valueOne.values, valueTwo.values, valueOne.dataType);
}

function compareDatasetValueList(valueOne, valueTwo) {
    return compareProperties(valueOne, valueTwo, ['dataType']) &&
        compareLists(valueOne.values, valueTwo.values, valueOne.dataType);
}

function compareDatasetMultiSelectList(valueOne, valueTwo) {
    return compareProperties(valueOne, valueTwo, ['dataType']) &&
        compareLists(valueOne.values, valueTwo.values, valueOne.dataType);
}

function compareDataSourceSingleValue(valueOne, valueTwo) {
    return compareProperties(valueOne, valueTwo, ['dataSourceId']);
}

function compareDataSourceMultipleValue(valueOne, valueTwo) {
    return compareProperties(valueOne, valueTwo, ['dataSourceId']);
}

function compareDataSourceValueList(valueOne, valueTwo) {
    return compareProperties(valueOne, valueTwo, ['dataSourceId']);
}

export function compareValueSets(valueOne, valueTwo) {
    if (valueOne === valueTwo) {
        return true;
    }
    if (!lodash.get(valueOne, '_isValueSet', false) ||
        !lodash.get(valueTwo, '_isValueSet', false)) {
        return lodash.eq(valueOne, valueTwo);
    }

    const valueSetOne = ValueSetRegistry.reconstituteDeserializedJson(valueOne);
    const valueSetTwo = ValueSetRegistry.reconstituteDeserializedJson(valueTwo);

    if (valueSetOne.type !== valueSetTwo.type) {
        return false;
    }

    const handler = comparisonHandlersByType[valueSetOne.type];
    return handler(valueSetOne, valueSetTwo);
}
